<script lang="ts">
/**
 * @author Fredrik Granerud
 * @description A select menu that allows for routing to a specific route.
 */
import type { HTMLAttributes } from 'vue'
import type { GatRouteLocation, AppRoute } from '@gat-ship/lib/router'
import type { IconValues } from '@gat-ship/icons'
import type { TooltipHintProps } from '@gat-ship/vue-components'

export type RouterSelectMenuItem = {
  /** The text to display in the select menu. */
  text: string
  /** The icon to display in the select menu. */
  icon: IconValues
  /** The route to navigate to when the item is clicked. */
  to: GatRouteLocation<AppRoute>
  /** The data testid to apply to the select menu item. */
  dataTestid?: string
  /** The hint to display in the select menu item. */
  hint?: TooltipHintProps
}

export interface RouterSelectMenuProps {
  /** The class to apply to the select menu. The default class is 'w-[256px]' */
  class?: HTMLAttributes['class']
  /** The items to display in the select menu. */
  items?: RouterSelectMenuItem[]
}
</script>

<script setup lang="ts">
import { SelectMenu, SelectMenuItem } from '../select-menu'
import { cn } from '@gat-ship/vue-components'

const props = defineProps<RouterSelectMenuProps>()
</script>
<template>
  <div :class="cn('flex w-[256px] shrink-0 flex-col gap-5', props.class)">
    <!-- Append elements before the select menu -->
    <slot name="append-select-menu" />
    <SelectMenu class="w-full" v-if="(items?.length ?? 0) > 0 || $slots['select-menu-items']">
      <slot name="select-menu-items">
        <template v-for="item in items" :key="item.text">
          <SelectMenuItem :to="item.to" :icon="item.icon" :data-testid="item.dataTestid" :hint="item.hint">{{ item.text
          }}</SelectMenuItem>
        </template>
      </slot>
    </SelectMenu>
    <!-- Append elements after the select menu -->
    <slot name="prepend-select-menu" />
  </div>
</template>
